import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { authMe, authLogout, getMyMenuRights } from '../api';
import { askPushOnLogin } from '../utils/pushNotifications';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // null  = chargement en cours
  // null (après chargement admin) = accès total (bypass)
  // []    = droits vides (tout masqué)
  // ['key1', ...] = droits spécifiques
  const [menuRights, setMenuRights] = useState(null);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      setUser(null);
      setMenuRights(null);
      return;
    }
    try {
      const res = await authMe();
      const u = res.data;
      setUser(u);
      // admin_org et super_admin : bypass total (null = voir tout)
      const isAdmin = u.role === 'admin_org' || u.role === 'super_admin';
      if (isAdmin) {
        setMenuRights(null);
      } else {
        try {
          const rightsRes = await getMyMenuRights();
          setMenuRights(rightsRes.data.menu_rights ?? []);
        } catch {
          setMenuRights([]);
        }
      }
      askPushOnLogin().catch(() => {});
    } catch (err) {
      console.error('[AUTH] checkAuth failed:', err?.response?.status);
      localStorage.removeItem('token');
      setUser(null);
      setMenuRights(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const logout = useCallback(async (options = {}) => {
    try { await authLogout(); } catch { /* ignore */ }
    localStorage.removeItem('token');
    if (options.sessionExpired) {
      sessionStorage.setItem('mbud_session_expired', '1');
    }
    setUser(null);
    setMenuRights(null);
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => {
      toast.error('Session expirée, veuillez vous reconnecter.');
      logout({ sessionExpired: true });
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [logout]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth, menuRights, setMenuRights }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
